import {
    defineComponent,
    h,
    defineAsyncComponent,
    type PropType,
    DefineComponent,
} from 'vue';
import type {SemanticColorName} from '@pds/style/types/color';

/**
 * 아이콘 컴포넌트 Props
 */
export interface IconProps {
    /** 아이콘 크기 (정사각형) */
    size?: number | string;
    /** 시멘틱 컬러 이름 */
    color?: SemanticColorName;
    /** 추가 클래스명 */
    class?: string | string[] | Record<string, boolean>;
}

/**
 * SVG 컴포넌트를 Props를 받는 아이콘 컴포넌트로 래핑 (Lazy Loading 지원)
 *
 * @example
 * ```TypeScript
 * export const IcnPdsApple = withIcon(() => import('./svg/icn_pds_apple.svg?component'));
 * ```
 *
 * @example
 * ```vue
 * <IcnPdsApple :size="16" color="foreground-secondary" class="my-icon" />
 * ```
 */
const withIcon = (importFn: () => Promise<any>) => {
    const AsyncSvgComponent = defineAsyncComponent(importFn);

    return defineComponent<IconProps>({
        name: 'IconWrapper',
        props: {
            size: {
                type: [Number, String] as PropType<number | string>,
                default: 20,
            },
            color: {
                type: String as PropType<SemanticColorName>,
                default: 'foreground-primary',
            },
            class: {
                type: [String, Array, Object] as PropType<
                    string | string[] | Record<string, boolean>
                >,
                default: '',
            },
        },
        setup(props, {attrs}) {
            return () => {
                const style = props.color
                    ? {
                          fill: `var(--pds-color-alias-${props.color})`,
                          '--icon-color': `var(--pds-color-alias-${props.color})`,
                      }
                    : undefined;

                return h(AsyncSvgComponent, {
                    width: props.size,
                    height: props.size,
                    class: ['pds-icon', props.class],
                    style,
                    ...attrs,
                });
            };
        },
    }) as DefineComponent<{}, {}, IconProps>;
};

export default withIcon;
